import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import api from '../../../services/api';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import PageContainer from '../../../common/components/PageContainer';
import useAlerta from '../../../common/components/useAlerta';
import AppSnackbar from '../../../common/components/AppSnackbar';
import { TIPOS_SERVICO, exigeRastreabilidadeResiduos, normalizarDatasOS } from '../osLabels';

function SectionTitle({ children }) {
  return (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
        {children}
      </Typography>
      <Divider sx={{ mb: 2 }} />
    </>
  );
}

function CadastroOS() {
  const { register, handleSubmit } = useForm({
    defaultValues: { dataEmissao: new Date().toISOString().slice(0, 10) },
  });
  const { alerta, mostrarAlerta, fecharAlerta } = useAlerta();
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState("");
  const [tipoServico, setTipoServico] = useState("");

  const residuos = exigeRastreabilidadeResiduos(tipoServico);

  useEffect(() => {
    async function fetchDataFuncionarios() {
      try {
        const response = await api.get(`/funcionario/lista`);
        setFuncionarios(response.data);
      } catch (error) {
        console.error('Erro ao buscar funcinarios:', error);
      }
    }
    fetchDataFuncionarios();
  }, []);

  const onSubmit = async (data) => {
    if (!funcionarioSelecionado) {
      mostrarAlerta('Selecione o responsável técnico!', 'error');
      return;
    }
    if (!tipoServico) {
      mostrarAlerta('Selecione o tipo de serviço!', 'error');
      return;
    }
    if (residuos && !data.numeroMtr) {
      mostrarAlerta('Informe o número do MTR: obrigatório para serviços que envolvem resíduos.', 'error');
      return;
    }

    const formData = normalizarDatasOS({
      ...data,
      tipoServico,
      responsavelId: funcionarioSelecionado,
    });

    try {
      await api.post('/os', formData);
      mostrarAlerta('Ordem de Serviço cadastrada com sucesso!', 'success');
      setTimeout(() => {
        window.location.href = '/os';
      }, 1500);
    } catch (error) {
      console.error(error);
      mostrarAlerta('Erro ao cadastrar Ordem de Serviço!', 'error');
    }
    setTimeout(fecharAlerta, 3000);
  };

  return (
    <PageContainer sx={{ maxWidth: 900 }}>
      <Paper component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
          Cadastro de Ordem de Serviço
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Documento de liberação de trabalho para desmonte industrial, conforme NR-1, NR-18 e rastreabilidade
          de resíduos (MTR/SINIR). A OS é criada como rascunho e só pode ser liberada para início depois de
          preenchida.
        </Typography>

        <SectionTitle>Identificação</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField {...register("descricao")} label="Descrição do serviço" placeholder="Descrição..." required fullWidth multiline minRows={2} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...register("empresa")} label="Empresa/Cliente" placeholder="Empresa..." required fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...register("local")} label="Local da execução" placeholder="Local..." required fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...register("cnpj")} label="CNPJ" placeholder="CNPJ..." fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...register("cpf")} label="CPF" placeholder="CPF..." fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...register("dataEmissao")} label="Data de emissão" type="date" InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Responsável técnico"
              value={funcionarioSelecionado}
              onChange={(e) => setFuncionarioSelecionado(e.target.value)}
              required
              fullWidth
            >
              <MenuItem value="">Selecione o responsável</MenuItem>
              {funcionarios.map(funcionario => (
                <MenuItem key={funcionario.id} value={funcionario.id}>
                  {funcionario.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Tipo de serviço"
              value={tipoServico}
              onChange={(e) => setTipoServico(e.target.value)}
              required
              fullWidth
            >
              <MenuItem value="">Selecione o tipo de serviço</MenuItem>
              {TIPOS_SERVICO.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>{tipo.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {tipoServico === 'OUTROS' && (
            <Grid item xs={12}>
              <TextField {...register("outrosServicosDescricao")} label="Descreva o tipo de serviço" fullWidth />
            </Grid>
          )}
        </Grid>

        <SectionTitle>Contrato</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField {...register("contratoNumero")} label="Número do contrato" fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...register("contratoDataAssinatura")} label="Data de assinatura" type="date" InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...register("contratoItem")} label="Item do contrato" fullWidth />
          </Grid>
        </Grid>

        <SectionTitle>Condições e normas (NR-1 / NR-18 / NR-35 / NR-33 / NR-12)</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              {...register("condicoesMeioAmbiente")}
              label="Condições de meio ambiente"
              helperText="Riscos ambientais e medidas de controle previstas para a atividade"
              fullWidth multiline minRows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...register("condicoesSeguranca")}
              label="Condições de segurança"
              helperText="Riscos, EPIs/EPCs exigidos e medidas preventivas (trabalho em altura, espaço confinado, máquinas, etc.)"
              fullWidth multiline minRows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...register("metodosProcessosEquipamentos")}
              label="Métodos, processos e equipamentos"
              fullWidth multiline minRows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...register("competenciasPessoal")}
              label="Competências de pessoal exigidas"
              helperText="Treinamentos e capacitações obrigatórios para a equipe executante"
              fullWidth multiline minRows={2}
            />
          </Grid>
        </Grid>

        <SectionTitle>Rastreabilidade de resíduos (PNRS / MTR nacional - SINIR)</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              {...register("numeroMtr")}
              label="Número do MTR"
              required={residuos}
              helperText={residuos ? "Obrigatório para gerenciamento/transporte de resíduos" : undefined}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...register("numeroSinir")} label="Número do SINIR" fullWidth />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button type="submit" variant="contained" color="secondary" size="large" sx={{ px: 6 }}>
            Cadastrar
          </Button>
        </Box>
      </Paper>

      <AppSnackbar alerta={alerta} onClose={fecharAlerta} />
    </PageContainer>
  );
}

export default CadastroOS;
